import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { ExternalLink, ShoppingBag } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import StoreSection from '@/components/StoreSection';
import CartDrawer from '@/components/CartDrawer';

const THEME_PRESETS = {
  Concrete: { bg: '#DCDDD8', text: '#1C1F26', cardBg: '#D3D4CE', border: '#1C1F26', muted: '#5A5D64' },
  Midnight: { bg: '#15171C', text: '#E7E8E3', cardBg: '#1E2128', border: '#2E323B', muted: '#9EA1A8' },
  Blush: { bg: '#E8D8D4', text: '#2A1514', cardBg: '#DFCECA', border: '#2A1514', muted: '#694F4D' },
};

function resolveFont(name) {
  return `'StandardAmp', '${name}', serif`;
}

function applyTheme(settings) {
  if (!settings) return;
  let t;
  if (settings.theme === 'Custom' && settings.custom_theme) {
    t = { bg: settings.custom_theme.bg, text: settings.custom_theme.text, cardBg: settings.custom_theme.bg, border: settings.custom_theme.text, muted: settings.custom_theme.text + '99' };
  } else {
    t = THEME_PRESETS[settings.theme] || THEME_PRESETS.Concrete;
  }
  const root = document.documentElement.style;
  root.setProperty('--bg-color', t.bg);
  root.setProperty('--text-color', t.text);
  root.setProperty('--card-bg', t.cardBg || t.bg);
  root.setProperty('--border-color', t.border);
  root.setProperty('--muted-color', t.muted);
  root.setProperty('--accent-color', settings.accent_color || '#2B3BE0');
  root.setProperty('--headline-font', resolveFont(settings.headline_font || 'Bodoni Moda'));
  root.setProperty('--body-font', `'StandardAmp', '${settings.body_font || 'Archivo'}', sans-serif`);
  document.body.style.backgroundColor = t.bg;
  document.body.style.color = t.text;
}

function getHeadlineStyle(settings) {
  return {
    fontFamily: 'var(--headline-font)',
    fontStyle: settings?.headline_italic !== false ? 'italic' : 'normal',
    textTransform: settings?.headline_uppercase !== false ? 'uppercase' : 'none',
  };
}

// ============ SECTION COMPONENTS ============
function TopStrip({ content, adminPreview }) {
  const d = content.top_strip || {};
  const { setOpen, itemCount } = useCart();
  const hasStore = content.store?.open || adminPreview;
  return (
    <div data-testid="top-strip" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex justify-between items-center" style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>
        <span data-testid="top-strip-left">{d.left}</span>
        <span data-testid="top-strip-center" className="hidden sm:inline">{d.center}</span>
        <div className="flex items-center gap-4">
          <span data-testid="top-strip-right">{d.right}</span>
          {hasStore && (
            <a href="#store" style={{ textDecoration: 'none', color: 'var(--text-color)', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = 'var(--accent-color)'} onMouseLeave={e => e.target.style.color = 'var(--text-color)'}>Shop</a>
          )}
          <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative flex items-center gap-1 transition-opacity hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-color)' }}>
            <ShoppingBag size={15} />
            {itemCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center text-white rounded-full" style={{ backgroundColor: 'var(--accent-color)', fontSize: '0.5rem', fontWeight: 700 }}>{itemCount}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Masthead({ content }) {
  const s = content.settings || {};
  return (
    <div data-testid="masthead" style={{ borderBottom: '1px solid var(--border-color)' }} className="py-4 sm:py-6 overflow-hidden">
      <h1 data-testid="masthead-title" className="text-center leading-none tracking-tight px-4" style={{ ...getHeadlineStyle(s), fontSize: 'clamp(2.5rem, 10vw, 9rem)', fontWeight: 900, lineHeight: 0.9 }}>
        {content.masthead}
      </h1>
    </div>
  );
}

function CoverLine({ text, label }) {
  return (
    <div className="flex flex-col gap-1">
      <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--headline-font)', fontSize: 'clamp(1rem, 2vw, 1.4rem)', fontStyle: 'italic', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>{text}</span>
    </div>
  );
}

function CoverSection({ content }) {
  const d = content.cover || {};
  const lines = d.lines || [];
  const layout = content.layout?.cover_layout || 'center';
  if (layout === 'full-width') {
    return (
      <section data-testid="cover-section" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="relative overflow-hidden" style={{ maxHeight: '80vh' }}>
          <img src={d.image_url} alt={d.image_alt || 'Cover'} className="w-full h-full object-cover" loading="eager" style={{ minHeight: '50vh' }} />
        </div>
        {d.caption && <p className="max-w-7xl mx-auto px-4 sm:px-8 py-3 text-center" style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>{d.caption}</p>}
      </section>
    );
  }
  return (
    <section data-testid="cover-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-16" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="hidden lg:flex lg:col-span-3 flex-col gap-8 justify-center">{lines.slice(0, 2).map((l, i) => <CoverLine key={i} text={l.text} label={l.label} />)}</div>
        <div className="lg:col-span-6">
          <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
            <img data-testid="cover-image" src={d.image_url} alt={d.image_alt || 'Cover'} className="w-full h-full object-cover" loading="eager" style={{ transition: 'transform 0.6s ease' }} onMouseEnter={e => e.target.style.transform = 'scale(1.02)'} onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
          </div>
          {d.caption && <p className="mt-3 text-center" style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>{d.caption}</p>}
        </div>
        <div className="hidden lg:flex lg:col-span-3 flex-col gap-8 justify-center">{lines.slice(2, 4).map((l, i) => <CoverLine key={i} text={l.text} label={l.label} />)}</div>
        <div className="lg:hidden grid grid-cols-2 gap-4 mt-4">{lines.map((l, i) => <CoverLine key={i} text={l.text} label={l.label} />)}</div>
      </div>
    </section>
  );
}

function MeetCreator({ content }) {
  const d = content.creator || {};
  const s = content.settings || {};
  const isRight = content.layout?.creator_layout === 'portrait-right';
  return (
    <section data-testid="meet-creator-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start ${isRight ? '' : ''}`}>
        <div className={`md:col-span-5 ${isRight ? 'md:order-2' : ''}`}>
          <div className="overflow-hidden" style={{ aspectRatio: '4/5' }}>
            <img data-testid="portrait-image" src={d.portrait_url} alt={d.portrait_alt || 'Creator portrait'} className="w-full h-full object-cover" loading="lazy" style={{ transition: 'transform 0.6s ease' }} onMouseEnter={e => e.target.style.transform = 'scale(1.02)'} onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
          </div>
        </div>
        <div className={`md:col-span-7 flex flex-col justify-center ${isRight ? 'md:order-1' : ''}`}>
          <div className="flex justify-between items-baseline pb-3 mb-8" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <h2 style={{ ...getHeadlineStyle(s), fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}>{d.section_title}</h2>
            <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Profile</span>
          </div>
          {d.bio?.map((p, i) => <p key={i} className="mb-5" style={{ fontFamily: 'var(--body-font)', fontSize: '0.95rem', lineHeight: 1.75 }}>{p}</p>)}
          {d.pull_quote && <blockquote className="mt-6 pl-5 py-2" style={{ borderLeft: '3px solid var(--accent-color)', fontFamily: 'var(--headline-font)', fontStyle: 'italic', fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', lineHeight: 1.4 }}>{d.pull_quote}</blockquote>}
        </div>
      </div>
    </section>
  );
}

function Lookbook({ content }) {
  const d = content.lookbook || {};
  const photos = d.photos || [];
  const s = content.settings || {};
  const layout = content.layout || {};
  const cols = layout.lookbook_columns || 3;
  const gridStyle = layout.lookbook_layout || 'staggered';
  const colClass = cols === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <section data-testid="lookbook-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className="flex justify-between items-baseline pb-3 mb-10" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <h2 style={{ ...getHeadlineStyle(s), fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}>{d.section_title}</h2>
        <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>{photos.length} Looks</span>
      </div>
      <div className={`grid grid-cols-1 ${colClass} gap-6 md:gap-10`}>
        {photos.map((photo, idx) => (
          <div key={photo.id || idx} data-testid={`lookbook-photo-${idx}`} className="group"
            style={{ marginTop: gridStyle === 'staggered' ? (idx % cols === 1 ? '2.5rem' : idx % cols === 2 ? '5rem' : '0') : '0' }}>
            <div className="overflow-hidden" style={{ aspectRatio: gridStyle === 'large-first' && idx === 0 ? '16/9' : '3/4' }}>
              <img src={photo.url} alt={photo.alt || photo.title} className="w-full h-full object-cover" loading="lazy" style={{ transition: 'transform 0.6s ease' }} onMouseEnter={e => e.target.style.transform = 'scale(1.03)'} onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
            </div>
            {photo.title && <p className="mt-3" style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>{photo.title}</p>}
            {photo.caption && <p className="mt-1" style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', lineHeight: 1.5, color: 'var(--muted-color)' }}>{photo.caption}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

function FollowAlong({ content }) {
  const links = content.social_links || [];
  const s = content.settings || {};
  const visibleLinks = useMemo(() => links.filter(l => l.url && l.url.trim()), [links]);
  const layout = content.layout?.follow_layout || 'large-text';
  if (layout === 'button-row') {
    return (
      <section data-testid="follow-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex justify-between items-baseline pb-3 mb-8" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h2 style={{ ...getHeadlineStyle(s), fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}>Follow Along</h2>
        </div>
        <div className="flex flex-wrap gap-3">{visibleLinks.map((link, idx) => (
          <a key={link.id || idx} href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`social-link-${link.platform?.toLowerCase()}`}
            className="px-6 py-3 text-xs uppercase tracking-widest font-semibold" style={{ border: '1px solid var(--border-color)', textDecoration: 'none', color: 'var(--text-color)', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--accent-color)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--accent-color)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-color)'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}>
            {link.platform}
          </a>
        ))}</div>
      </section>
    );
  }
  return (
    <section data-testid="follow-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className="flex justify-between items-baseline pb-3 mb-8" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <h2 style={{ ...getHeadlineStyle(s), fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}>Follow Along</h2>
        <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Connect</span>
      </div>
      <div style={{ borderTop: '1px solid var(--border-color)' }}>
        {visibleLinks.map((link, idx) => (
          <a key={link.id || idx} href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`social-link-${link.platform?.toLowerCase()}`}
            className="flex items-center justify-between py-5 group" style={{ borderBottom: '1px solid var(--border-color)', textDecoration: 'none', color: 'var(--text-color)', transition: 'color 0.2s ease' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-color)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-color)'}>
            <span style={{ fontFamily: 'var(--headline-font)', fontStyle: 'italic', fontSize: 'clamp(1.2rem, 3vw, 2rem)', textTransform: 'uppercase' }}>{link.platform}</span>
            <span className="flex items-center gap-3"><span style={{ fontFamily: 'var(--body-font)', fontSize: '0.8rem', color: 'var(--muted-color)' }}>{link.handle}</span><ExternalLink size={16} style={{ opacity: 0.5 }} /></span>
          </a>
        ))}
      </div>
    </section>
  );
}

function BrandPartnerships({ content }) {
  const d = content.partnerships || {};
  const s = content.settings || {};
  return (
    <section data-testid="partnerships-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div className="flex justify-between items-baseline pb-3 mb-8" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <h2 style={{ ...getHeadlineStyle(s), fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}>{d.section_title}</h2>
        <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Collaborate</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        <div>
          <p className="mb-6" style={{ fontFamily: 'var(--body-font)', fontSize: '0.95rem', lineHeight: 1.75 }}>{d.intro}</p>
          <ul className="space-y-3">
            {d.offerings?.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3" style={{ fontFamily: 'var(--body-font)', fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--accent-color)', fontWeight: 700, fontSize: '0.7rem', marginTop: '0.3rem' }}>&#9646;</span>{item}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center items-start md:items-end">
          <a data-testid="email-cta-button" href={`mailto:${d.contact_email || ''}`} className="inline-block px-8 py-4 text-sm font-semibold uppercase tracking-widest"
            style={{ backgroundColor: 'var(--accent-color)', color: '#fff', fontFamily: 'var(--body-font)', letterSpacing: '0.15em', transition: 'opacity 0.2s ease', textDecoration: 'none' }}
            onMouseEnter={e => e.target.style.opacity = '0.85'} onMouseLeave={e => e.target.style.opacity = '1'}>
            {d.button_label || 'Email about a deal'}
          </a>
          {d.contact_email && <p className="mt-3" style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--muted-color)' }}>{d.contact_email}</p>}
        </div>
      </div>
    </section>
  );
}

function Footer({ content }) {
  const d = content.footer || {};
  return (
    <footer data-testid="footer-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex flex-wrap justify-center gap-4 mb-4">
        {[{slug:'shipping',label:'Shipping'},{slug:'returns',label:'Returns & Refunds'},{slug:'privacy',label:'Privacy'},{slug:'terms',label:'Terms'}].map(p => (
          <Link key={p.slug} to={`/legal/${p.slug}`} style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', color: 'var(--muted-color)', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'opacity 0.2s', opacity: 0.6 }}
            onMouseEnter={e => e.target.style.opacity = '1'} onMouseLeave={e => e.target.style.opacity = '0.6'}>{p.label}</Link>
        ))}
      </div>
      <div className="flex justify-center items-center">
        <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--muted-color)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{d.text}</p>
      </div>
    </footer>
  );
}

// ============ SECTION REGISTRY ============
const SECTIONS = { top_strip: TopStrip, masthead: Masthead, cover: CoverSection, creator: MeetCreator, lookbook: Lookbook, store: StoreSection, social_links: FollowAlong, partnerships: BrandPartnerships, footer: Footer };

export default function PublicPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    const url = isPreview ? '/content?preview=1' : '/content';
    api.get(url).then(({ data }) => {
      setContent(data);
      applyTheme(data.settings);
      if (data.seo?.title) document.title = data.seo.title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc && data.seo?.description) desc.setAttribute('content', data.seo.description);
    }).catch(console.error).finally(() => setLoading(false));
  }, [isPreview]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-color)' }}><div style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Loading issue...</div></div>;
  if (!content) return null;

  const order = content.layout?.sections_order || Object.keys(SECTIONS);
  const hidden = content.layout?.hidden_sections || [];

  return (
    <div data-testid="public-page" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', minHeight: '100vh' }}>
      {isPreview && <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-black text-center py-1 text-xs font-bold uppercase tracking-widest">Preview Mode — Not Published</div>}
      {isAdmin && !content.store?.open && (
        <div className="fixed top-0 left-0 right-0 z-50 text-center py-1.5 text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: '#2563eb', color: '#fff' }}>
          Admin Store Preview — Public visitors see "Coming Soon"
        </div>
      )}
      {order.filter(s => !hidden.includes(s)).map(key => {
        const Comp = SECTIONS[key];
        if (!Comp) return null;
        return <Comp key={key} content={content} adminPreview={isAdmin && !content.store?.open} />;
      })}
      <CartDrawer />
    </div>
  );
}
